/**
 * Mobile Audio Player
 * A dedicated audio player for mobile devices
 */

(function() {
    'use strict';

    let initialized = false;
    let elements = null;
    // Player view state: 'mini' | 'full' | 'extended'
    let playerState = 'mini';
    let isDragging = false;
    let miniDragging = false;
    let extDragging = false;
    let currentVariantId = null;
    
    // Volume fade settings
    let savedVolume = 100;
    let isFading = false;
    const FADE_DURATION = 300; // ms
    const FADE_STEPS = 15;

    // Check if we're on mobile
    function isMobile() {
        return window.innerWidth <= 768;
    }

    // Wait for DOM
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    function init() {
        // Only initialize on mobile
        if (!isMobile()) {
            console.log('[MobilePlayer] Not mobile, skipping init');
            return;
        }

        const player = document.getElementById('mobile-audio-player');
        if (!player) {
            console.log('[MobilePlayer] Player element not found');
            return;
        }

        if (initialized) {
            console.log('[MobilePlayer] Already initialized');
            return;
        }

        elements = {
            player: player,
            cover: player.querySelector('.mobile-player-cover'),
            title: player.querySelector('.mobile-player-title'),
            meta: player.querySelector('.mobile-player-meta'),
            currentTime: player.querySelector('.mobile-player-current'),
            duration: player.querySelector('.mobile-player-duration'),
            progressFill: player.querySelector('.mobile-player-progress-fill'),
            progressContainer: player.querySelector('.mobile-player-progress'),
            miniProgress: player.querySelector('.mobile-player-mini-progress'),
            miniProgressTrack: player.querySelector('.mobile-player-mini-progress-track'),
            miniProgressFill: player.querySelector('.mobile-player-mini-progress-fill'),
            explorer: player.querySelector('.mobile-player-explorer'),
            playBtn: player.querySelector('.mobile-player-play'),
            miniPlayBtn: player.querySelector('.mobile-player-mini-play'),
            miniPrevBtn: player.querySelector('.mobile-player-mini-prev'),
            miniNextBtn: player.querySelector('.mobile-player-mini-next'),
            prevBtn: player.querySelector('.mobile-player-prev'),
            nextBtn: player.querySelector('.mobile-player-next'),
            cartBtns: Array.from(player.querySelectorAll('.mobile-player-cart')),
            closeBtn: player.querySelector('.mobile-player-close'),
            expandBtn: player.querySelector('.mobile-player-expand'),
            extendBtn: player.querySelector('.mobile-player-extend-btn'),
            extCollapseBtn: player.querySelector('.mobile-player-ext-collapse'),
            // Extended (detailed library) view
            extended: player.querySelector('.mobile-player-extended'),
            extCarousel: player.querySelector('.mobile-player-ext-carousel'),
            extTitle: player.querySelector('.mobile-player-ext-title'),
            extArtist: player.querySelector('.mobile-player-ext-artist'),
            extMeta: player.querySelector('.mobile-player-ext-meta'),
            extCurrent: player.querySelector('.mobile-player-ext-current'),
            extDuration: player.querySelector('.mobile-player-ext-duration'),
            extProgress: player.querySelector('.mobile-player-ext-progress'),
            extProgressTrack: player.querySelector('.mobile-player-ext-progress-track'),
            extProgressFill: player.querySelector('.mobile-player-ext-progress-fill'),
            extPlayBtn: player.querySelector('.mobile-player-ext-play'),
            extPrevBtn: player.querySelector('.mobile-player-ext-prev'),
            extNextBtn: player.querySelector('.mobile-player-ext-next'),
            extList: player.querySelector('.mobile-player-ext-list'),
            extHero: player.querySelector('.mobile-player-ext-hero'),
            extDivider: player.querySelector('.mobile-player-ext-divider'),
            backdrop: document.getElementById('mobile-player-backdrop'),
            // Extended filters
            extFilters: player.querySelector('.mobile-player-ext-filters'),
            extCount: player.querySelector('.mobile-player-ext-count'),
            filterSelects: {
                genre: player.querySelector('.mobile-player-ext-filter[data-filter="genre"]'),
                gender: player.querySelector('.mobile-player-ext-filter[data-filter="gender"]'),
                key: player.querySelector('.mobile-player-ext-filter[data-filter="key"]')
            },
            bpmWrap: player.querySelector('.mobile-player-ext-bpm'),
            bpmMin: player.querySelector('.mobile-player-ext-bpm-min'),
            bpmMax: player.querySelector('.mobile-player-ext-bpm-max'),
            bpmFill: player.querySelector('.mobile-player-ext-bpm-fill'),
            bpmNum: player.querySelector('.mobile-player-ext-bpm-num')
        };

        // Start collapsed (mini) by default
        player.classList.add('is-mini');
        playerState = 'mini';

        // Keep the player touchable when a theme drawer (cart, menu…) is open
        keepPlayerInteractive();

        // Bind events
        bindEvents();

        // Listen for custom events from main audio player
        bindCustomEvents();

        // Also poll for Amplitude state changes as fallback
        startStatePolling();

        initialized = true;
        console.log('[MobilePlayer] Initialized successfully');
    }

    // When a theme drawer opens (cart, menu, etc.), the theme marks body-level
    // siblings as `inert` to trap focus. The mobile player is one of those
    // siblings, so the extended player stayed visible (z-index:1000, above the
    // drawer and #drawerCover) but became completely untouchable. Strip any
    // `inert` the drawer logic adds so the player keeps working while a drawer
    // is open.
    function keepPlayerInteractive() {
        const player = elements && elements.player;
        if (!player || typeof MutationObserver === 'undefined') return;

        const stripInert = function() {
            if (player.hasAttribute('inert')) player.removeAttribute('inert');
        };

        stripInert();
        new MutationObserver(stripInert).observe(player, {
            attributes: true,
            attributeFilter: ['inert']
        });
    }

    // ========== UI Update Functions ==========
    
    function updateUI(song) {
        if (!elements || !song) return;

        currentVariantId = song.id;

        // Update info
        elements.cover.src = song.cover_art_url || '';
        elements.title.textContent = song.name || 'Unknown';
        elements.title.href = song.product_url || '#';

        // Build a subtle meta line: BPM · key only
        const metaText = buildMeta(song);
        if (elements.meta) elements.meta.textContent = metaText;

        // Extended hero (big now-playing)
        if (elements.extTitle) {
            elements.extTitle.textContent = song.name || 'Unknown';
            elements.extTitle.href = song.product_url || '#';
        }
        if (elements.extArtist) updateArtistLink(song);
        if (elements.extMeta) elements.extMeta.textContent = metaText;

        // Store variant ID on every cart button (expanded + collapsed + extended)
        elements.cartBtns.forEach(btn => { btn.dataset.variantId = song.id; });

        // Update cart button
        updateCartButton();

        // Keep the explorer + extended list + artwork carousel in sync
        refreshExplorer();
        refreshExtendedList();
        refreshExtCarousel(false);
        refreshFilters();
    }

    function buildMeta(song) {
        const parts = [];
        if (song.bpm) parts.push(`${song.bpm} BPM`);
        if (song.key) parts.push(song.key);
        return parts.join('  ·  ');
    }

    // Show the artist (collection title, not the raw vendor) and link it to the
    // artist's collection page. Falls back to vendor data when a song predates
    // the collection metadata.
    function updateArtistLink(song) {
        const name = song.collection_title || song.album || '';
        const handle = song.collection_handle || song.vendor_handle || '';
        elements.extArtist.textContent = name;
        if (handle) {
            elements.extArtist.href = `/collections/${handle}`;
        } else if (name) {
            elements.extArtist.href = `/collections/vendors?q=${encodeURIComponent(name)}`;
        } else {
            elements.extArtist.removeAttribute('href');
        }
    }

    function updateProgress() {
        if (!elements) return;
        
        try {
            const percentage = Amplitude.getSongPlayedPercentage();
            if (elements.progressFill) {
                elements.progressFill.style.width = `${percentage}%`;
            }
            if (elements.miniProgressFill) {
                elements.miniProgressFill.style.width = `${percentage}%`;
            }
            if (elements.extProgressFill) {
                elements.extProgressFill.style.width = `${percentage}%`;
            }

            // Update time displays
            const current = Amplitude.getSongPlayedSeconds();
            const duration = Amplitude.getSongDuration();

            if (elements.currentTime) {
                elements.currentTime.textContent = formatTime(current);
            }
            if (elements.duration) {
                elements.duration.textContent = formatTime(duration);
            }
            if (elements.extCurrent) {
                elements.extCurrent.textContent = formatTime(current);
            }
            if (elements.extDuration) {
                elements.extDuration.textContent = formatTime(duration);
            }
        } catch (e) {
            // Amplitude not ready yet
        }
    }

    function updatePlayState(isPlaying) {
        if (!elements) return;

        if (isPlaying) {
            elements.playBtn?.classList.add('is-playing');
            elements.miniPlayBtn?.classList.add('is-playing');
            elements.extPlayBtn?.classList.add('is-playing');
            elements.player.classList.add('is-playing-now');
        } else {
            elements.playBtn?.classList.remove('is-playing');
            elements.miniPlayBtn?.classList.remove('is-playing');
            elements.extPlayBtn?.classList.remove('is-playing');
            elements.player.classList.remove('is-playing-now');
        }
    }

    function updateCartButton() {
        if (!elements || !elements.cartBtns) return;

        const variantId = parseInt(currentVariantId);
        const isInCart = window.primeVocalsCartItems && 
                         window.primeVocalsCartItems.includes(variantId);

        let html;
        if (isInCart) {
            html = `
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Added
            `;
        } else {
            let price = '';
            try {
                const song = Amplitude.getActiveSongMetadata();
                price = song && song.price ? song.price : '';
            } catch (e) {}

            html = `
                ${price}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
            `;
        }

        elements.cartBtns.forEach(btn => {
            btn.classList.toggle('in-cart', isInCart);
            btn.innerHTML = html;
        });
    }

    function showPlayer() {
        if (!elements) return;
        elements.player.classList.add('is-visible');
        // Build the heavy expanded DOM during idle time so the first expand
        // doesn't have to do it mid-animation.
        schedulePrebuild();
        console.log('[MobilePlayer] Showing player');
    }

    // Pre-render the expensive expanded library + carousel ahead of time. The
    // build functions are guarded by song count, so this is a cheap no-op once
    // everything is already built.
    let prebuildScheduled = false;
    function prebuildExtended() {
        buildExtendedList();
        buildExtCarousel();
    }
    function schedulePrebuild() {
        if (prebuildScheduled) return;
        prebuildScheduled = true;
        const run = function() {
            prebuildScheduled = false;
            try { prebuildExtended(); } catch (e) {}
        };
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(run, { timeout: 1500 });
        } else {
            setTimeout(run, 300);
        }
    }

    function hidePlayer() {
        if (!elements) return;

        if (elements.backdrop) elements.backdrop.classList.remove('is-active');
        
        try {
            if (Amplitude.getPlayerState() === 'playing') {
                // Fade out then hide
                fadeOutAndPause();
                setTimeout(() => {
                    elements.player.classList.remove('is-visible');
                }, FADE_DURATION + 50);
            } else {
                elements.player.classList.remove('is-visible');
            }
        } catch (e) {
            elements.player.classList.remove('is-visible');
        }
    }

    function setPlayerState(state) {
        if (!elements) return;
        playerState = state;

        elements.player.classList.toggle('is-mini', state === 'mini');
        elements.player.classList.toggle('is-extended', state === 'extended');

        // Show the tap-to-collapse scrim only while the library is expanded
        if (elements.backdrop) {
            elements.backdrop.classList.toggle('is-active', state === 'extended');
        }

        if (state === 'mini') {
            refreshExplorer();
            setTimeout(updateExplorerActive, 320);
        } else if (state === 'extended') {
            // Make sure the DOM exists (usually already prebuilt during idle), but
            // let the expand animation start before doing the lighter sync work so
            // the first frames stay smooth.
            prebuildExtended();
            requestAnimationFrame(() => {
                updateExtendedActive(false);
                updateRowCarts();
                refreshExtCarousel(false);
                refreshFilters();
            });
            setTimeout(() => {
                updateExtendedActive(true);
                refreshExtCarousel(true);
                applyFilters();
            }, 360);
        }
    }

    // Swipe up always opens the extended library; the expand handle / swipe
    // down step the player back down through full -> mini.
    function openExtended() {
        setPlayerState('extended');
    }

    function stepDown() {
        if (playerState === 'extended') {
            setPlayerState('full');
        } else if (playerState === 'full') {
            setPlayerState('mini');
        }
    }

    // Expand handle click: collapse the extended view, otherwise toggle mini/full.
    function toggleMini() {
        if (playerState === 'extended') {
            setPlayerState('full');
        } else if (playerState === 'mini') {
            setPlayerState('full');
        } else {
            setPlayerState('mini');
        }
    }

    function formatTime(seconds) {
        if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    }

    // ========== Track Explorer ==========

    let explorerSongCount = -1;

    function getSongs() {
        try {
            return Amplitude.getSongs() || [];
        } catch (e) {
            return [];
        }
    }

    // (Re)build the explorer strip when the playlist size changes.
    function buildExplorer() {
        if (!elements || !elements.explorer) return;

        const songs = getSongs();
        if (songs.length === explorerSongCount) return; // already built
        explorerSongCount = songs.length;

        const frag = document.createDocumentFragment();
        songs.forEach((song, index) => {
            const item = document.createElement('button');
            item.className = 'mobile-player-explorer-item';
            item.type = 'button';
            item.dataset.index = index;
            item.setAttribute('aria-label', song.name || `Track ${index + 1}`);

            const img = document.createElement('img');
            img.loading = 'lazy';
            img.decoding = 'async';
            img.alt = '';
            img.src = song.cover_art_url || '';
            item.appendChild(img);

            const eq = document.createElement('span');
            eq.className = 'mobile-player-explorer-eq';
            eq.innerHTML = '<span></span><span></span><span></span>';
            item.appendChild(eq);

            frag.appendChild(item);
        });

        elements.explorer.innerHTML = '';
        elements.explorer.appendChild(frag);
    }

    function refreshExplorer() {
        if (!elements || !elements.explorer) return;
        buildExplorer();
        updateExplorerActive();
    }

    function updateExplorerActive() {
        if (!elements || !elements.explorer) return;

        let activeIndex = -1;
        try {
            activeIndex = Amplitude.getActiveIndex();
        } catch (e) {
            return;
        }

        const container = elements.explorer;
        const items = container.children;
        for (let i = 0; i < items.length; i++) {
            const isActive = parseInt(items[i].dataset.index, 10) === activeIndex;
            items[i].classList.toggle('is-active', isActive);
            if (isActive) {
                // Scroll only the explorer horizontally (avoid scrolling the page,
                // since the player is fixed to the bottom of the viewport).
                const cRect = container.getBoundingClientRect();
                const iRect = items[i].getBoundingClientRect();
                const delta = (iRect.left - cRect.left) - (container.clientWidth / 2) + (iRect.width / 2);
                container.scrollTo({ left: container.scrollLeft + delta, behavior: 'smooth' });
            }
        }
    }

    // Tap detection: ignore clicks that are really horizontal scrolls/drags so
    // browsing the film strip never switches tracks by accident.
    let explorerTouchStartX = 0;
    let explorerTouchStartY = 0;
    let explorerMoved = false;
    const EXPLORER_TAP_THRESHOLD = 10; // px

    function handleExplorerTouchStart(e) {
        const touch = e.touches[0];
        if (!touch) return;
        explorerTouchStartX = touch.clientX;
        explorerTouchStartY = touch.clientY;
        explorerMoved = false;
    }

    function handleExplorerTouchMove(e) {
        const touch = e.touches[0];
        if (!touch) return;
        const dx = Math.abs(touch.clientX - explorerTouchStartX);
        const dy = Math.abs(touch.clientY - explorerTouchStartY);
        if (dx > EXPLORER_TAP_THRESHOLD || dy > EXPLORER_TAP_THRESHOLD) {
            explorerMoved = true;
        }
    }

    function playExplorerItem(item) {
        const index = parseInt(item.dataset.index, 10);
        if (isNaN(index)) return;

        try {
            Amplitude.playSongAtIndex(index);
            showPlayer();
            updatePlayState(true);
            updateExplorerActive();
        } catch (err) {
            console.error('[MobilePlayer] Explorer play error:', err);
        }
    }

    function handleExplorerClick(e) {
        // A scroll gesture ended here, not a real tap — ignore it.
        if (explorerMoved) {
            explorerMoved = false;
            return;
        }

        const item = e.target.closest('.mobile-player-explorer-item');
        if (!item) return;

        playExplorerItem(item);
    }

    // ========== Extended Library List ==========

    const CART_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>';
    const CHECK_ICON = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>';

    let extListSongCount = -1;

    function buildExtendedList() {
        if (!elements || !elements.extList) return;

        const songs = getSongs();
        if (songs.length === extListSongCount) return; // already built
        extListSongCount = songs.length;

        const frag = document.createDocumentFragment();
        songs.forEach((song, index) => {
            const row = document.createElement('div');
            row.className = 'mobile-player-ext-row';
            row.dataset.index = index;

            const meta = buildMeta(song);
            const priceHtml = song.price ? `<span class="mobile-player-ext-row-price">${song.price}</span>` : '';

            row.innerHTML = `
                <span class="mobile-player-ext-row-cover">
                    <img loading="lazy" decoding="async" alt="" src="${song.cover_art_url || ''}">
                    <span class="mobile-player-ext-row-eq"><span></span><span></span><span></span></span>
                </span>
                <span class="mobile-player-ext-row-info">
                    <span class="mobile-player-ext-row-title">${escapeHtml(song.name || 'Unknown')}</span>
                    <span class="mobile-player-ext-row-meta">${escapeHtml(meta)}</span>
                </span>
                ${priceHtml}
                <button class="mobile-player-ext-row-cart" data-variant-id="${song.id}" aria-label="Add to cart">${CART_ICON}</button>
            `;

            frag.appendChild(row);
        });

        const empty = document.createElement('div');
        empty.className = 'mobile-player-ext-empty';
        empty.textContent = 'No tracks match these filters';
        frag.appendChild(empty);

        elements.extList.innerHTML = '';
        elements.extList.appendChild(frag);
        updateRowCarts();
    }

    function escapeHtml(str) {
        return String(str).replace(/[&<>"']/g, c => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c]));
    }

    function refreshExtendedList() {
        if (!elements || !elements.extList) return;
        buildExtendedList();
        updateExtendedActive(false);
        updateRowCarts();
    }

    function updateExtendedActive(scrollIntoView) {
        if (!elements || !elements.extList) return;

        let activeIndex = -1;
        try {
            activeIndex = Amplitude.getActiveIndex();
        } catch (e) {
            return;
        }

        const rows = elements.extList.children;
        for (let i = 0; i < rows.length; i++) {
            const isActive = parseInt(rows[i].dataset.index, 10) === activeIndex;
            rows[i].classList.toggle('is-active', isActive);
            if (isActive && scrollIntoView) {
                const container = elements.extList;
                const cRect = container.getBoundingClientRect();
                const rRect = rows[i].getBoundingClientRect();
                const delta = (rRect.top - cRect.top) - (container.clientHeight / 2) + (rRect.height / 2);
                container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' });
            }
        }
    }

    function updateRowCarts() {
        if (!elements || !elements.extList) return;
        const carts = elements.extList.querySelectorAll('.mobile-player-ext-row-cart');
        carts.forEach(btn => {
            const id = parseInt(btn.dataset.variantId, 10);
            const isInCart = window.primeVocalsCartItems &&
                             window.primeVocalsCartItems.includes(id);
            btn.classList.toggle('in-cart', !!isInCart);
            btn.innerHTML = isInCart ? CHECK_ICON : CART_ICON;
        });
    }

    function handleExtListClick(e) {
        const cartBtn = e.target.closest('.mobile-player-ext-row-cart');
        if (cartBtn) {
            e.stopPropagation();
            toggleVariantInCart(cartBtn.dataset.variantId);
            return;
        }

        const row = e.target.closest('.mobile-player-ext-row');
        if (!row) return;

        const index = parseInt(row.dataset.index, 10);
        if (isNaN(index)) return;

        try {
            Amplitude.playSongAtIndex(index);
            showPlayer();
            updatePlayState(true);
            updateExtendedActive(false);
        } catch (err) {
            console.error('[MobilePlayer] Extended play error:', err);
        }
    }

    // ========== Extended artwork carousel (swipe prev / next) ==========

    let extCarouselSongCount = -1;
    let extCarouselSettleTimer = null;
    let extCarTouchX = 0;
    let extCarTouchY = 0;
    let extCarMoved = false;
    let suppressCarouselPlay = false;
    let suppressCarouselTimer = null;
    const EXT_CAR_TAP_THRESHOLD = 10; // px

    function getActiveIndexSafe() {
        try { return Amplitude.getActiveIndex(); } catch (e) { return -1; }
    }

    // (Re)build the carousel only when the playlist size changes (keeps scroll).
    function buildExtCarousel() {
        if (!elements || !elements.extCarousel) return;

        const songs = getSongs();
        if (songs.length === extCarouselSongCount) return; // already built
        extCarouselSongCount = songs.length;

        const frag = document.createDocumentFragment();
        songs.forEach((song, index) => {
            const slide = document.createElement('button');
            slide.type = 'button';
            slide.className = 'mobile-player-ext-slide';
            slide.dataset.index = index;
            slide.setAttribute('aria-label', song.name || `Track ${index + 1}`);

            const img = document.createElement('img');
            img.loading = 'lazy';
            img.decoding = 'async';
            img.alt = '';
            img.src = song.cover_art_url || '';
            slide.appendChild(img);

            frag.appendChild(slide);
        });

        elements.extCarousel.innerHTML = '';
        elements.extCarousel.appendChild(frag);
    }

    function refreshExtCarousel(smooth) {
        if (!elements || !elements.extCarousel) return;
        buildExtCarousel();
        centerExtCarousel(getActiveIndexSafe(), smooth === true);
        updateExtCarouselActive();
    }

    // Scroll so the slide at `index` sits dead-center.
    function centerExtCarousel(index, smooth) {
        const c = elements && elements.extCarousel;
        if (!c) return;
        centerOnSlide(c.children[index], smooth);
    }

    // Programmatic centering must not be mistaken for a user swipe (which would
    // start playing whatever slide it lands on), so suppress the settle-play
    // until the scroll finishes.
    function centerOnSlide(slide, smooth) {
        const c = elements && elements.extCarousel;
        if (!c || !slide) return;

        const cRect = c.getBoundingClientRect();
        const sRect = slide.getBoundingClientRect();
        const delta = (sRect.left - cRect.left) - (c.clientWidth - slide.clientWidth) / 2;

        suppressCarouselPlay = true;
        clearTimeout(suppressCarouselTimer);
        c.scrollTo({ left: c.scrollLeft + delta, behavior: smooth ? 'smooth' : 'auto' });
        suppressCarouselTimer = setTimeout(function() {
            suppressCarouselPlay = false;
        }, smooth ? 500 : 120);
    }

    // Mark whichever (visible) slide is nearest the center as active; return its
    // real song index.
    function updateExtCarouselActive() {
        const c = elements && elements.extCarousel;
        if (!c || !c.children.length) return -1;

        const cRect = c.getBoundingClientRect();
        const center = cRect.left + cRect.width / 2;

        let bestEl = null;
        let bestDist = Infinity;
        for (let i = 0; i < c.children.length; i++) {
            const el = c.children[i];
            if (el.classList.contains('is-hidden')) { el.classList.remove('is-active'); continue; }
            const sRect = el.getBoundingClientRect();
            const dist = Math.abs((sRect.left + sRect.width / 2) - center);
            if (dist < bestDist) { bestDist = dist; bestEl = el; }
        }
        for (let i = 0; i < c.children.length; i++) {
            if (c.children[i].classList.contains('is-hidden')) continue;
            c.children[i].classList.toggle('is-active', c.children[i] === bestEl);
        }
        return bestEl ? parseInt(bestEl.dataset.index, 10) : -1;
    }

    // Live blur/scale feedback while swiping; play the centered track on settle.
    function handleExtCarouselScroll() {
        updateExtCarouselActive();
        clearTimeout(extCarouselSettleTimer);
        extCarouselSettleTimer = setTimeout(extCarouselSettled, 140);
    }

    function extCarouselSettled() {
        const idx = updateExtCarouselActive();
        if (idx < 0) return;
        if (suppressCarouselPlay) return;        // we scrolled it programmatically
        if (idx === getActiveIndexSafe()) return; // already playing it
        try {
            Amplitude.playSongAtIndex(idx);
            showPlayer();
            updatePlayState(true);
        } catch (e) {
            console.error('[MobilePlayer] Carousel play error:', e);
        }
    }

    function handleExtCarTouchStart(e) {
        const t = e.touches[0];
        if (!t) return;
        extCarTouchX = t.clientX;
        extCarTouchY = t.clientY;
        extCarMoved = false;
    }

    function handleExtCarTouchMove(e) {
        const t = e.touches[0];
        if (!t) return;
        if (Math.abs(t.clientX - extCarTouchX) > EXT_CAR_TAP_THRESHOLD ||
            Math.abs(t.clientY - extCarTouchY) > EXT_CAR_TAP_THRESHOLD) {
            extCarMoved = true;
        }
    }

    // Tap a peeking neighbour to play it; tap the centered artwork to toggle
    // play/pause. Swipes are ignored here.
    function handleExtCarouselClick(e) {
        if (extCarMoved) { extCarMoved = false; return; }

        const slide = e.target.closest('.mobile-player-ext-slide');
        if (!slide) return;

        const index = parseInt(slide.dataset.index, 10);
        if (isNaN(index)) return;

        if (index === getActiveIndexSafe()) {
            handlePlayPause();
        } else {
            try {
                Amplitude.playSongAtIndex(index);
                showPlayer();
                updatePlayState(true);
            } catch (err) {
                console.error('[MobilePlayer] Carousel tap play error:', err);
            }
        }
    }

    // ========== Extended filters (genre / gender / key / BPM) ==========

    let filterState = { genre: '', gender: '', key: '', minBpm: null, maxBpm: null };
    let bpmBounds = { min: 0, max: 0 };
    let filtersBuilt = false;
    let filterSongCount = -1;

    function getSongGenres(song) {
        if (!song || !song.genre) return [];
        return String(song.genre).split(',').map(s => s.trim()).filter(Boolean);
    }

    function sortValues(arr) {
        return arr.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }

    // Build the option lists from the library's distinct values. Rebuilds only
    // when the playlist size changes so user selections survive UI refreshes.
    function buildExtFilters() {
        if (!elements || !elements.extFilters) return;
        const songs = getSongs();
        if (filtersBuilt && songs.length === filterSongCount) return;
        filterSongCount = songs.length;

        const genres = new Set();
        const genders = new Set();
        const keys = new Set();
        let minB = Infinity;
        let maxB = -Infinity;

        songs.forEach(song => {
            getSongGenres(song).forEach(g => genres.add(g));
            if (song.gender) genders.add(String(song.gender).trim());
            if (song.key) keys.add(String(song.key).trim());
            const b = parseInt(song.bpm, 10);
            if (!isNaN(b)) { if (b < minB) minB = b; if (b > maxB) maxB = b; }
        });

        populateFilterSelect(elements.filterSelects.genre, 'Genre', sortValues([...genres]));
        populateFilterSelect(elements.filterSelects.gender, 'Gender', sortValues([...genders]));
        populateFilterSelect(elements.filterSelects.key, 'Key', sortValues([...keys]));

        if (minB === Infinity) { minB = 0; maxB = 0; }
        bpmBounds = { min: minB, max: maxB };
        setupBpmRange();

        filtersBuilt = true;
    }

    function populateFilterSelect(sel, label, values) {
        if (!sel) return;
        const current = sel.value;
        sel.innerHTML = '';
        const def = document.createElement('option');
        def.value = '';
        def.textContent = label;
        sel.appendChild(def);
        values.forEach(v => {
            const o = document.createElement('option');
            o.value = v;
            o.textContent = v;
            sel.appendChild(o);
        });
        sel.classList.toggle('is-empty', values.length === 0);
        if (current && values.indexOf(current) !== -1) sel.value = current;
    }

    function setupBpmRange() {
        if (!elements.bpmMin || !elements.bpmMax) return;
        const hasRange = bpmBounds.max > bpmBounds.min;
        elements.bpmWrap && elements.bpmWrap.classList.toggle('is-empty', !hasRange);
        if (!hasRange) return;

        [elements.bpmMin, elements.bpmMax].forEach(r => {
            r.min = bpmBounds.min;
            r.max = bpmBounds.max;
            r.step = 1;
        });
        elements.bpmMin.value = filterState.minBpm === null ? bpmBounds.min : filterState.minBpm;
        elements.bpmMax.value = filterState.maxBpm === null ? bpmBounds.max : filterState.maxBpm;
        updateBpmVisual();
    }

    function updateBpmVisual() {
        if (!elements.bpmMin || !elements.bpmMax) return;
        const span = (bpmBounds.max - bpmBounds.min) || 1;
        const lo = parseInt(elements.bpmMin.value, 10);
        const hi = parseInt(elements.bpmMax.value, 10);
        const left = ((lo - bpmBounds.min) / span) * 100;
        const right = ((hi - bpmBounds.min) / span) * 100;
        if (elements.bpmFill) {
            elements.bpmFill.style.left = left + '%';
            elements.bpmFill.style.width = Math.max(0, right - left) + '%';
        }
        const active = !(lo === bpmBounds.min && hi === bpmBounds.max);
        if (elements.bpmNum) elements.bpmNum.textContent = active ? (lo + '\u2013' + hi) : '';
        elements.bpmWrap && elements.bpmWrap.classList.toggle('is-set', active);
    }

    function commitBpm() {
        const lo = parseInt(elements.bpmMin.value, 10);
        const hi = parseInt(elements.bpmMax.value, 10);
        filterState.minBpm = (lo === bpmBounds.min) ? null : lo;
        filterState.maxBpm = (hi === bpmBounds.max) ? null : hi;
        updateBpmVisual();
        applyFilters();
    }

    function songMatchesFilters(song) {
        if (!song) return false;
        const f = filterState;

        if (f.genre) {
            const gs = getSongGenres(song).map(s => s.toLowerCase());
            if (gs.indexOf(f.genre.toLowerCase()) === -1) return false;
        }
        if (f.gender && String(song.gender || '').trim().toLowerCase() !== f.gender.toLowerCase()) return false;
        if (f.key && String(song.key || '').trim().toLowerCase() !== f.key.toLowerCase()) return false;
        if (f.minBpm !== null || f.maxBpm !== null) {
            const b = parseInt(song.bpm, 10);
            if (isNaN(b)) return false;
            if (f.minBpm !== null && b < f.minBpm) return false;
            if (f.maxBpm !== null && b > f.maxBpm) return false;
        }
        return true;
    }

    function hasActiveFilters() {
        const f = filterState;
        return !!(f.genre || f.gender || f.key || f.minBpm !== null || f.maxBpm !== null);
    }

    // Show/hide rows + carousel slides in real time to match the active filters.
    function applyFilters() {
        if (!elements) return;
        const songs = getSongs();
        let visible = 0;

        if (elements.extList) {
            const rows = elements.extList.querySelectorAll('.mobile-player-ext-row');
            rows.forEach(row => {
                const idx = parseInt(row.dataset.index, 10);
                const match = songMatchesFilters(songs[idx]);
                row.classList.toggle('is-hidden', !match);
                if (match) visible++;
            });
            elements.extList.classList.toggle('is-empty', visible === 0);
        }

        if (elements.extCarousel) {
            const slides = elements.extCarousel.children;
            for (let i = 0; i < slides.length; i++) {
                const idx = parseInt(slides[i].dataset.index, 10);
                slides[i].classList.toggle('is-hidden', !songMatchesFilters(songs[idx]));
            }
        }

        if (elements.extCount) {
            elements.extCount.textContent = hasActiveFilters() ? (visible + ' / ' + songs.length) : '';
        }

        ['genre', 'gender', 'key'].forEach(k => {
            const sel = elements.filterSelects[k];
            if (sel) sel.classList.toggle('is-set', !!sel.value);
        });

        recenterCarouselAfterFilter();
    }

    function recenterCarouselAfterFilter() {
        const c = elements && elements.extCarousel;
        if (!c || !c.children.length) return;

        const activeIdx = getActiveIndexSafe();
        const activeSlide = c.children[activeIdx];
        if (activeSlide && !activeSlide.classList.contains('is-hidden')) {
            centerOnSlide(activeSlide, true);
        } else {
            for (let i = 0; i < c.children.length; i++) {
                if (!c.children[i].classList.contains('is-hidden')) {
                    centerOnSlide(c.children[i], true);
                    break;
                }
            }
        }
        updateExtCarouselActive();
    }

    function refreshFilters() {
        buildExtFilters();
        applyFilters();
    }

    // ========== Event Handlers ==========

    // Volume fade out effect
    function fadeOutAndPause() {
        if (isFading) return;
        isFading = true;

        try {
            savedVolume = Amplitude.getVolume() || 100;
            let currentVol = savedVolume;
            const stepSize = currentVol / FADE_STEPS;
            const stepTime = FADE_DURATION / FADE_STEPS;

            const fadeInterval = setInterval(() => {
                currentVol -= stepSize;
                
                if (currentVol <= 0) {
                    clearInterval(fadeInterval);
                    Amplitude.setVolume(0);
                    Amplitude.pause();
                    // Restore volume immediately after pause so next play starts normal
                    setTimeout(() => {
                        Amplitude.setVolume(savedVolume);
                        isFading = false;
                    }, 50);
                } else {
                    Amplitude.setVolume(Math.round(currentVol));
                }
            }, stepTime);
        } catch (e) {
            // Fallback to normal pause
            Amplitude.pause();
            isFading = false;
        }
    }

    // Volume fade in effect
    function fadeInAndPlay() {
        if (isFading) return;
        isFading = true;

        try {
            const targetVolume = savedVolume || 100;
            Amplitude.setVolume(0);
            Amplitude.play();

            let currentVol = 0;
            const stepSize = targetVolume / FADE_STEPS;
            const stepTime = FADE_DURATION / FADE_STEPS;

            const fadeInterval = setInterval(() => {
                currentVol += stepSize;
                
                if (currentVol >= targetVolume) {
                    clearInterval(fadeInterval);
                    Amplitude.setVolume(targetVolume);
                    isFading = false;
                } else {
                    Amplitude.setVolume(Math.round(currentVol));
                }
            }, stepTime);
        } catch (e) {
            // Fallback to normal play
            Amplitude.play();
            isFading = false;
        }
    }

    function handlePlayPause() {
        if (isFading) return; // Prevent spam clicking during fade
        
        try {
            if (Amplitude.getPlayerState() === 'playing') {
                fadeOutAndPause();
            } else {
                fadeInAndPlay();
            }
        } catch (e) {
            console.error('[MobilePlayer] PlayPause error:', e);
        }
    }

    function handlePrev() {
        try {
            Amplitude.prev();
        } catch (e) {}
    }

    function handleNext() {
        try {
            Amplitude.next();
        } catch (e) {}
    }

    // Seek using the geometry of the supplied track element.
    function seekFromEvent(e, measureEl) {
        if (!measureEl) return;

        const rect = measureEl.getBoundingClientRect();
        if (!rect.width) return;

        let clientX;
        if (e.type.startsWith('touch')) {
            const touch = e.touches[0] || e.changedTouches[0];
            if (!touch) return;
            clientX = touch.clientX;
        } else {
            clientX = e.clientX;
        }

        const percentage = Math.max(0, Math.min(100,
            ((clientX - rect.left) / rect.width) * 100
        ));

        try {
            Amplitude.setSongPlayedPercentage(percentage);
        } catch (err) {}
    }

    function handleProgressSeek(e) {
        if (!elements) return;
        seekFromEvent(e, elements.progressContainer);
    }

    function handleMiniProgressSeek(e) {
        if (!elements) return;
        seekFromEvent(e, elements.miniProgressTrack);
    }

    function handleExtProgressSeek(e) {
        if (!elements) return;
        seekFromEvent(e, elements.extProgressTrack);
    }

    function syncCartUI() {
        updateCartButton();
        updateRowCarts();
    }

    // Shared add/remove for any variant id (used by player + per-row buttons).
    function toggleVariantInCart(rawVariantId) {
        const variantId = parseInt(rawVariantId);
        if (!variantId) return;

        const isInCart = window.primeVocalsCartItems &&
                         window.primeVocalsCartItems.includes(variantId);

        // Optimistic update
        if (isInCart) {
            window.primeVocalsCartItems = (window.primeVocalsCartItems || []).filter(id => id !== variantId);
        } else {
            window.primeVocalsCartItems = [...(window.primeVocalsCartItems || []), variantId];
        }

        syncCartUI();
        document.dispatchEvent(new CustomEvent('primevocals:cart-updated', {
            detail: { cartItems: window.primeVocalsCartItems }
        }));

        // Network request. Shopify's AJAX cart accepts a variant id as `id`.
        const url = isInCart ? '/cart/change.js' : '/cart/add.js';
        const body = isInCart
            ? { id: String(variantId), quantity: 0 }
            : { id: variantId, quantity: 1 };

        const rollback = (reason) => {
            console.error('[MobilePlayer] Cart error:', reason);
            if (isInCart) {
                window.primeVocalsCartItems = [...(window.primeVocalsCartItems || []), variantId];
            } else {
                window.primeVocalsCartItems = (window.primeVocalsCartItems || []).filter(id => id !== variantId);
            }
            syncCartUI();
            document.dispatchEvent(new CustomEvent('primevocals:cart-updated', {
                detail: { cartItems: window.primeVocalsCartItems }
            }));
        };

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(body)
        })
        .then(res => {
            // Shopify returns non-2xx (e.g. 422) when the add/remove fails.
            // Treat that as an error so we don't falsely show "Added".
            if (!res.ok) {
                return res.json().catch(() => null).then(data => {
                    throw new Error((data && (data.description || data.message)) || ('HTTP ' + res.status));
                });
            }
            return res.json().catch(() => null);
        })
        .then(() => {
            // Tell the theme's cart drawer / count to refresh and re-sync our
            // cart state from the server (handled in custom-audio-player.js).
            document.body.dispatchEvent(new CustomEvent('label:modalcart:afteradditem', {
                bubbles: true,
                detail: { openDrawer: false }
            }));
            document.body.dispatchEvent(new CustomEvent('label:modalcart:itemcountupdate', {
                bubbles: true
            }));
        })
        .catch(rollback);
    }

    function handleCart(e) {
        if (!elements) return;
        const btn = e && e.currentTarget ? e.currentTarget : null;
        toggleVariantInCart(btn ? btn.dataset.variantId : currentVariantId);
    }

    // ========== Event Bindings ==========

    function bindEvents() {
        if (!elements) return;

        // Play buttons
        elements.playBtn?.addEventListener('click', handlePlayPause);
        elements.miniPlayBtn?.addEventListener('click', handlePlayPause);
        elements.extPlayBtn?.addEventListener('click', handlePlayPause);

        // Nav buttons
        elements.prevBtn?.addEventListener('click', handlePrev);
        elements.nextBtn?.addEventListener('click', handleNext);
        elements.miniPrevBtn?.addEventListener('click', handlePrev);
        elements.miniNextBtn?.addEventListener('click', handleNext);
        elements.extPrevBtn?.addEventListener('click', handlePrev);
        elements.extNextBtn?.addEventListener('click', handleNext);

        // Track explorer (tap to play, but ignore scroll/drag gestures)
        if (elements.explorer) {
            elements.explorer.addEventListener('touchstart', handleExplorerTouchStart, { passive: true });
            elements.explorer.addEventListener('touchmove', handleExplorerTouchMove, { passive: true });
            elements.explorer.addEventListener('click', handleExplorerClick);
        }

        // Extended detailed library list (tap row to play, tap cart to add)
        elements.extList?.addEventListener('click', handleExtListClick);

        // Extended artwork carousel (swipe to change track, tap to play/pause)
        if (elements.extCarousel) {
            elements.extCarousel.addEventListener('scroll', handleExtCarouselScroll, { passive: true });
            elements.extCarousel.addEventListener('touchstart', handleExtCarTouchStart, { passive: true });
            elements.extCarousel.addEventListener('touchmove', handleExtCarTouchMove, { passive: true });
            elements.extCarousel.addEventListener('click', handleExtCarouselClick);
        }

        // Extended filters (genre / gender / key / BPM) — apply in real time
        ['genre', 'gender', 'key'].forEach(k => {
            const sel = elements.filterSelects && elements.filterSelects[k];
            if (!sel) return;
            sel.addEventListener('change', function() {
                filterState[k] = sel.value;
                applyFilters();
            });
        });

        if (elements.bpmMin && elements.bpmMax) {
            elements.bpmMin.addEventListener('input', function() {
                if (parseInt(elements.bpmMin.value, 10) > parseInt(elements.bpmMax.value, 10)) {
                    elements.bpmMin.value = elements.bpmMax.value;
                }
                commitBpm();
            });
            elements.bpmMax.addEventListener('input', function() {
                if (parseInt(elements.bpmMax.value, 10) < parseInt(elements.bpmMin.value, 10)) {
                    elements.bpmMax.value = elements.bpmMin.value;
                }
                commitBpm();
            });
        }

        // Extended progress seek (tap or drag)
        if (elements.extProgress) {
            elements.extProgress.addEventListener('touchstart', function(e) {
                extDragging = true;
                handleExtProgressSeek(e);
            }, { passive: true });

            elements.extProgress.addEventListener('touchmove', function(e) {
                if (extDragging) {
                    handleExtProgressSeek(e);
                }
            }, { passive: true });

            elements.extProgress.addEventListener('touchend', function() {
                extDragging = false;
            });

            elements.extProgress.addEventListener('click', handleExtProgressSeek);
        }

        // Close
        elements.closeBtn?.addEventListener('click', hidePlayer);

        // Expand/collapse
        elements.expandBtn?.addEventListener('click', toggleMini);

        // Open the extended library / collapse it back
        elements.extendBtn?.addEventListener('click', openExtended);
        elements.extCollapseBtn?.addEventListener('click', stepDown);

        // Tapping the area above the expanded player collapses it to normal view
        elements.backdrop?.addEventListener('click', function() {
            if (playerState === 'extended') setPlayerState('full');
        });

        // Progress seek - touch events
        if (elements.progressContainer) {
            elements.progressContainer.addEventListener('touchstart', function(e) {
                isDragging = true;
                handleProgressSeek(e);
            }, { passive: true });

            elements.progressContainer.addEventListener('touchmove', function(e) {
                if (isDragging) {
                    handleProgressSeek(e);
                }
            }, { passive: true });

            elements.progressContainer.addEventListener('touchend', function() {
                isDragging = false;
            });

            // Progress seek - click
            elements.progressContainer.addEventListener('click', handleProgressSeek);
        }

        // Mini progress seek (collapsed view) - tap or drag along the bar.
        // Its own drag flag keeps it independent from the explorer's scroll.
        if (elements.miniProgress) {
            elements.miniProgress.addEventListener('touchstart', function(e) {
                miniDragging = true;
                handleMiniProgressSeek(e);
            }, { passive: true });

            elements.miniProgress.addEventListener('touchmove', function(e) {
                if (miniDragging) {
                    handleMiniProgressSeek(e);
                }
            }, { passive: true });

            elements.miniProgress.addEventListener('touchend', function() {
                miniDragging = false;
            });

            elements.miniProgress.addEventListener('click', handleMiniProgressSeek);
        }

        // Cart (expanded + collapsed buttons)
        elements.cartBtns.forEach(btn => btn.addEventListener('click', handleCart));

        // Swipe gestures
        bindSwipeGestures();

        // Draggable divider to resize the hero vs. library compartments
        bindHeroDivider();
    }

    function bindHeroDivider() {
        if (!elements || !elements.extDivider || !elements.extHero || !elements.extended) return;

        const MIN_HERO = 220;  // keep the artwork + now-playing info visible
        const MIN_LIST = 150;  // always leave room for the library list

        let dragging = false;
        let startY = 0;
        let startHeroH = 0;
        let maxHeroH = 0;

        const getY = (e) => (e.touches && e.touches[0] ? e.touches[0].clientY : e.clientY);

        function onStart(e) {
            dragging = true;
            startY = getY(e);
            startHeroH = elements.extHero.getBoundingClientRect().height;
            // The library list is flex:1, so its current height is the slack the
            // hero can grow into. The hero may expand by however much the list can
            // give up before hitting MIN_LIST (drag down), or shrink to MIN_HERO
            // (drag up).
            const listH = elements.extList ? elements.extList.getBoundingClientRect().height : 0;
            maxHeroH = startHeroH + Math.max(0, listH - MIN_LIST);

            elements.extended.classList.add('is-hero-resized', 'is-resizing');
            elements.extended.style.setProperty('--ext-hero-h', startHeroH + 'px');
            if (e.cancelable) e.preventDefault();
        }

        function onMove(e) {
            if (!dragging) return;
            const delta = getY(e) - startY;
            let next = startHeroH + delta;
            next = Math.max(MIN_HERO, Math.min(maxHeroH, next));
            elements.extended.style.setProperty('--ext-hero-h', next + 'px');
            if (e.cancelable) e.preventDefault();
        }

        function onEnd() {
            if (!dragging) return;
            dragging = false;
            // Re-enable easing so the artwork settles with a smooth squeeze
            elements.extended.classList.remove('is-resizing');
        }

        elements.extDivider.addEventListener('touchstart', onStart, { passive: false });
        elements.extDivider.addEventListener('touchmove', onMove, { passive: false });
        elements.extDivider.addEventListener('touchend', onEnd, { passive: true });
        elements.extDivider.addEventListener('touchcancel', onEnd, { passive: true });

        // Pointer support for desktop / stylus drags
        elements.extDivider.addEventListener('pointerdown', function(e) {
            if (e.pointerType === 'touch') return; // handled by touch events above
            onStart(e);
            elements.extDivider.setPointerCapture?.(e.pointerId);
        });
        elements.extDivider.addEventListener('pointermove', function(e) {
            if (e.pointerType === 'touch') return;
            onMove(e);
        });
        elements.extDivider.addEventListener('pointerup', onEnd);
        elements.extDivider.addEventListener('pointercancel', onEnd);
    }

    function bindSwipeGestures() {
        if (!elements || !elements.player) return;

        let touchStartY = 0;
        let touchStartX = 0;
        let touchStartTime = 0;
        let tracking = false;
        let claimed = false;  // we've decided this is a vertical swipe for the player
        let dragging = false; // live, finger-following pull-down in progress
        let settleTimer = null;

        // Pull down at least this far to commit one step toward the normal form.
        const COLLAPSE_THRESHOLD = 80;

        // Start a swipe from the player "chrome". When expanded, the entire
        // panel is a valid grab area (hero, header, filters, padding…) so the
        // collapse gesture is never lost to the page behind it. The divider has
        // its own resize gesture and the list scrolls on its own, so both are
        // excluded.
        const isSwipeZone = (target) => {
            if (target.closest('.mobile-player-ext-divider') ||
                target.closest('.mobile-player-ext-list')) {
                return false;
            }
            return target.closest('.mobile-player-expand') ||
                target.closest('.mobile-player-top') ||
                target.closest('.mobile-player-extended');
        };

        const isInteractive = (target) =>
            target.closest('button, a, input, select, .mobile-player-progress, .mobile-player-mini-progress, .mobile-player-ext-progress, .mobile-player-ext-bpm');

        // The player can still collapse a step while extended or full.
        const canStepDown = () => playerState === 'extended' || playerState === 'full';

        // Move the panel with the finger (1:1, with soft resistance past the
        // threshold) and gently fade the expanded library as it is pulled away.
        function applyLive(dy) {
            const t = dy <= COLLAPSE_THRESHOLD
                ? dy
                : COLLAPSE_THRESHOLD + (dy - COLLAPSE_THRESHOLD) * 0.4;
            elements.player.style.transition = 'none';
            elements.player.style.transform = 'translateY(' + t + 'px)';
            if (playerState === 'extended' && elements.extended) {
                elements.extended.style.opacity = String(Math.max(0.45, 1 - dy / 520));
            }
        }

        // Animate the panel back to rest; optionally commit one step down.
        function settle(commit) {
            if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
            elements.player.style.transition = 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)';
            elements.player.style.transform = '';
            if (elements.extended) elements.extended.style.opacity = '';
            settleTimer = setTimeout(function() {
                elements.player.style.transition = '';
                settleTimer = null;
            }, 480);
            if (commit) stepDown();
        }

        elements.player.addEventListener('touchstart', function(e) {
            if (isSwipeZone(e.target) && !isInteractive(e.target)) {
                touchStartY = e.touches[0].clientY;
                touchStartX = e.touches[0].clientX;
                touchStartTime = Date.now();
                tracking = true;
                claimed = false;
                dragging = false;
            } else {
                tracking = false;
            }
        }, { passive: true });

        // Non-passive so we can stop the page from scrolling once we recognise a
        // vertical swipe — otherwise the page scroll "steals" the gesture.
        elements.player.addEventListener('touchmove', function(e) {
            if (!tracking) return;

            const touch = e.touches[0];
            const deltaY = touch.clientY - touchStartY;
            const deltaX = touch.clientX - touchStartX;

            if (!claimed && Math.abs(deltaY) > 8 && Math.abs(deltaY) > Math.abs(deltaX)) {
                claimed = true;
                // A downward pull that can collapse becomes a live, gradual drag.
                dragging = deltaY > 0 && canStepDown();
            }

            if (!claimed) return;
            if (e.cancelable) e.preventDefault();

            if (dragging) {
                applyLive(Math.max(0, deltaY));
            }
        }, { passive: false });

        elements.player.addEventListener('touchend', function(e) {
            if (!tracking) return;
            tracking = false;

            const touchEndY = e.changedTouches[0].clientY;
            const deltaY = touchEndY - touchStartY;
            const deltaTime = Date.now() - touchStartTime;
            const flick = deltaTime < 350 && Math.abs(deltaY) > 40;

            if (dragging) {
                // Finger-following collapse: commit if pulled far enough or flicked.
                settle(deltaY > COLLAPSE_THRESHOLD || (flick && deltaY > 0));
            } else if (Math.abs(deltaY) > 40 && (claimed || deltaTime < 500)) {
                if (deltaY < 0) {
                    openExtended();
                } else {
                    stepDown();
                }
            }

            touchStartY = 0;
            claimed = false;
            dragging = false;
        }, { passive: true });

        elements.player.addEventListener('touchcancel', function() {
            if (!tracking) return;
            tracking = false;
            if (dragging) settle(false);
            claimed = false;
            dragging = false;
        }, { passive: true });
    }

    function bindCustomEvents() {
        // Listen for song changes
        document.addEventListener('primevocals:player-song-change', function(e) {
            if (!isMobile()) return;
            if (e.detail && e.detail.activeSong) {
                updateUI(e.detail.activeSong);
            }
        });

        // Listen for play - THIS IS THE KEY EVENT
        document.addEventListener('primevocals:player-play', function(e) {
            if (!isMobile()) return;
            console.log('[MobilePlayer] Received play event');
            showPlayer();
            updatePlayState(true);
            if (e.detail && e.detail.activeSong) {
                updateUI(e.detail.activeSong);
            }
        });

        // Listen for pause
        document.addEventListener('primevocals:player-pause', function() {
            if (!isMobile()) return;
            updatePlayState(false);
        });

        // Listen for cart updates
        document.addEventListener('primevocals:cart-updated', function() {
            if (!isMobile()) return;
            syncCartUI();
        });
    }

    function startStatePolling() {
        let lastState = null;
        let lastSongUrl = null;
        let pollInterval = null;
        let idleCount = 0;

        function poll() {
            if (!isMobile() || !elements) return;

            try {
                const state = Amplitude.getPlayerState();
                const song = Amplitude.getActiveSongMetadata();
                const songUrl = song ? song.url : null;

                if (state !== lastState) {
                    if (state === 'playing') {
                        showPlayer();
                        updatePlayState(true);
                        if (song) updateUI(song);
                        idleCount = 0;
                        switchRate(250);
                    } else if (state === 'paused') {
                        updatePlayState(false);
                        switchRate(2000);
                    }
                    lastState = state;
                }

                if (songUrl !== lastSongUrl && song) {
                    updateUI(song);
                    lastSongUrl = songUrl;
                }

                if (state === 'playing') {
                    updateProgress();
                    idleCount = 0;
                } else {
                    idleCount++;
                    if (idleCount > 30) switchRate(5000);
                }
            } catch (e) {}
        }

        function switchRate(ms) {
            if (pollInterval) clearInterval(pollInterval);
            pollInterval = setInterval(poll, ms);
        }

        switchRate(2000);
    }

    // Re-check on resize (in case user rotates device)
    window.addEventListener('resize', function() {
        if (isMobile() && !initialized) {
            init();
        }
    });

})();
